import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminGrowthSnapshotCategoryReflectionCreateAction,
  adminGrowthSnapshotCategoryReflectionGetAction,
  adminGrowthSnapshotCategoryReflectionUpdateAction,
  adminGrowthSnapshotsListAction
} from "../Action/action";

const CATEGORIES = [
  { value: "awareness", label: "Awareness" },
  { value: "connection", label: "Connection" },
  { value: "emotional_skills", label: "Emotional Skills" },
  { value: "parent_confidence", label: "Parent Confidence" },
  { value: "family_calm", label: "Family Calm" }
];

const LEVELS = [
  { value: "significant_growth", label: "Significant Growth" },
  { value: "growing", label: "Growing" },
  { value: "holding_steady", label: "Holding Steady" },
  { value: "gentle_focus", label: "A Gentle Focus" }
];

export default function CategoryReflectionForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { reflectionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    growth_snapshot_id: searchParams.get("snapshot_id") || "",
    category_key: "awareness",
    category_title: "Awareness",
    reflection_level: "significant_growth",
    reflection_label: "Significant Growth",
    subheading: "What we're noticing...",
    body_text: "",
    display_order: "1",
    status: 1
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotsListAction({ page: 1, per_page: 100 });
        if (!cancelled) setSnapshots(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !reflectionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminGrowthSnapshotCategoryReflectionGetAction(reflectionId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          toast.error(res?.message || "Not found", { toastId: "gs-cat-form" });
          navigate("/reflections-screens/category-reflections");
          return;
        }
        const d = res.data;
        setForm({
          growth_snapshot_id: String(d.growth_snapshot_id || ""),
          category_key: d.category_key || "awareness",
          category_title: d.category_title || "",
          reflection_level: d.reflection_level || "significant_growth",
          reflection_label: d.reflection_label || "",
          subheading: d.subheading || "",
          body_text: d.body_text || "",
          display_order: d.display_order != null ? String(d.display_order) : "0",
          status: Number(d.status) === 0 ? 0 : 1
        });
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Failed to load", {
          toastId: "gs-cat-form"
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, reflectionId, navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onCategoryKeyChange = (key) => {
    const cat = CATEGORIES.find((c) => c.value === key);
    setForm((prev) => ({
      ...prev,
      category_key: key,
      category_title: cat ? cat.label : prev.category_title
    }));
  };

  const onLevelChange = (level) => {
    const lvl = LEVELS.find((l) => l.value === level);
    setForm((prev) => ({
      ...prev,
      reflection_level: level,
      reflection_label: lvl ? lvl.label : prev.reflection_label
    }));
  };

  const listPath = form.growth_snapshot_id
    ? `/reflections-screens/category-reflections?snapshot_id=${form.growth_snapshot_id}`
    : "/reflections-screens/category-reflections";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.growth_snapshot_id) {
      toast.error("Select a growth snapshot", { toastId: "gs-cat-form" });
      return;
    }
    if (!String(form.body_text || "").trim() && !isEdit) {
      // allow empty on create for templates, but warn softly — still save
    }
    setSaving(true);
    try {
      const payload = {
        category_key: form.category_key,
        category_title: form.category_title.trim(),
        reflection_level: form.reflection_level,
        reflection_label: form.reflection_label.trim(),
        subheading: form.subheading || null,
        body_text: form.body_text || "",
        display_order: Number(form.display_order) || 0,
        status: Number(form.status) === 1 ? 1 : 0
      };
      const res = isEdit
        ? await adminGrowthSnapshotCategoryReflectionUpdateAction(reflectionId, payload)
        : await adminGrowthSnapshotCategoryReflectionCreateAction(form.growth_snapshot_id, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Updated" : "Created", { toastId: "gs-cat-form" });
      navigate(listPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-cat-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/reflections-screens">Reflections & screens</Link>
          <span>›</span>
          <Link to={listPath}>Category reflections</Link>
          <span>›</span>
          <span>{isEdit ? "Edit" : "Add"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to={listPath}>
          ← Back
        </Link>
      </div>

      <form onSubmit={onSubmit}>
        <div className="card mb-6">
          <h2 className="section-title">{isEdit ? "Edit category reflection" : "Add category reflection"}</h2>

          <div className="form-group">
            <label>
              Growth snapshot <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={form.growth_snapshot_id}
              disabled={isEdit}
              onChange={(e) => setField("growth_snapshot_id", e.target.value)}
              required
            >
              <option value="">Select…</option>
              {snapshots.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.title || `Snapshot #${s.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category key</label>
              <select
                className="form-control"
                value={form.category_key}
                onChange={(e) => onCategoryKeyChange(e.target.value)}
                disabled={isEdit}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Category title</label>
              <input
                className="form-control"
                value={form.category_title}
                onChange={(e) => setField("category_title", e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Reflection level</label>
              <select
                className="form-control"
                value={form.reflection_level}
                onChange={(e) => onLevelChange(e.target.value)}
                disabled={isEdit}
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Reflection label</label>
              <input
                className="form-control"
                value={form.reflection_label}
                onChange={(e) => setField("reflection_label", e.target.value)}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Subheading</label>
              <input
                className="form-control"
                value={form.subheading}
                onChange={(e) => setField("subheading", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Display order</label>
              <input
                className="form-control"
                type="number"
                min="0"
                value={form.display_order}
                onChange={(e) => setField("display_order", e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setField("status", Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Body text</label>
            <textarea
              className="form-control"
              rows={6}
              value={form.body_text}
              onChange={(e) => setField("body_text", e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to={listPath}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
